import React, { useContext, useEffect } from "react";
import { useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import L from "leaflet";
import "leaflet.tilelayer.colorfilter";
import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMap,
  LayersControl,
  ScaleControl,
} from "react-leaflet";
import {
  formatNumberLocale,
  timestampToDatetimeString,
} from "../../utils/formatting";
import { gettext } from "i18n";
import { useAuth } from "state/auth";
import { ALERT_LEVEL_RED, GET_MEASUREMENT_STATION_MEAN } from "utils/stations";
import AppContext from "theme/context/Context";

React.useLayoutEffect = React.useEffect; // Fix for SSR
var mapboxgl = require("mapbox-gl/dist/mapbox-gl.js");

const MAPBOX_ACCESS_TOKEN =
  "pk.eyJ1IjoibWl0dGF1c2d1cnUiLCJhIjoiY2x5NGV2ZWM5MDIzdjJpczdxY2E4bnpqYSJ9.p9STTCdy-L7SbrR3LXS0RQ";
mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

const mapMarker = L.icon({
  iconUrl: `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABkAAAApCAYAAADAk4LOAAAACXBIWXMAAAFgAAABYAEg2RPaAAADpElEQVRYCZ1XS1LbQBBtybIdiMEJKSpUqihgEW/xDdARyAnirOIl3MBH8NK7mBvkBpFv4Gy9IRSpFIQiRPyNfqkeZkY9HwmFt7Lm06+7p/vN2MmyDIrQ6QebALAHAD4AbFuWfQeAAACGs5H/w5jlsJJw4wMA+GhMFuMA99jIDJJOP+ihZwDQFmNuowWO1wS3viDXpdEdZPEc0odruj0EgN5s5H8tJOEEX8R3rbkMtcU34NTqhe5nSQTJ7Tkk80s6/Gk28scGiULguFBffgdufdEwWoQ0uoXo8hdAlooVH0REjISfwZSlyHGh0V5n6aHAtKTxXI5g6nQnMH0P4bEgwtR18Yw8Pj8QZ4ARUAI0Hl+fQZZGisGEBVwHr7XKzox57DXZ/ij8Cdwe2u057z9/wygOxRl4S2vSUHx1oucaMQGAHTrgtdag9mK5aN+Wx/uAAQ9Zenp/SRce4TpaNbQK4+sTcGqeTB/aIXv3XN5oj2VKqii++U0JunpZ8urxee4hvjqVc2hHpBDXuKKT9XMgVYJ1/1fPGSeaikzgmWWkMIi9bVf8UhotXxzORn5gWFchI8QyttlzjS0qpsaIGY2MMsujV/AUSdcY0dDpB6/EiOPYzclR1CI5mOez3ekHvrFLxa7cR5pTscfrXjk0Vhm5V2PqLUWnH3R5GbPGpMVD7E1ckXesKBQ7AS/vmQ1c0+kHuxpBj98lTCm8pbc5QRJRdZ6qHb/wGryXq3Lxszv+5gySuwvxueXySwYvHEjuQ9ofTGKYlrmK1EsCHMd5SoD7mZ1HHFCBHLNbMEshvrugqWLn01hpVVJhFgVGkDvK7hR6n2B+d9C7xsqWsbkqHv4cCsWezEb+o2SR+SFweUBxfA5wH7kShjKt2vWL57Px3GhIFEezkb8pxvUWHYhotAfCk2AtkEcxoOttrxUWDR5svb1emSQKj0WXK1HYIgFREbiBqmoZcB2RkbE+byMZiosorVgAZF1ID7yQhEs38wa7nUqNDezdlavC2HbBGSQkGgZ8uJVBmzeiKCRRpEa9ilWghORVeGB7BxeSKF5xqbFBkxBrFKUk/JHA7ppENQaCnCjthK+3opCEYyANztXmZN858cDYWSUSHk3A311GAZDvo6deNKUk1EsqnJoQlkYBNlmxQZeaMgmxoUokICoHDce351RCCiuKoirJWEgNOYvQplM2VCLhUqF7jf94rW9kHVUjQeheV4riv0i4ZOzzz/2y/+0KAOAfr4EE4HpCFhwAAAAASUVORK5CYII=`,
  // IMPORTANT: anchor to the bottom-center tip
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

const redMapMarker = L.icon({
  iconUrl: `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADAAAAAwCAYAAABXAvmHAAAACXBIWXMAAAsTAAALEwEAmpwYAAADGUlEQVR4nO2aS0hVURSG/8pepDWM7AFFBZk1CdTuXeucHko2qAbRpGkkiaLUJCcNgmjeoIggEGleNGkShRSRSJNUGkVBZVH0VkrF/tjn3m5iaXuf1z1CCxZcLpd//9866yz22ecC/6MQVKyloIWKHgoeUfCOggkqxql4S0U/Bd1UHGMO1chKUNBMxV0qJqmgZZrf3qagsbzGBX0Opv+eggepgrARKyi4Ftn4nyDdzKEqWfMeNlHwJHbzWsoh+tiYlPltFLxJ0DyLV2KYHrbEaz6HagpeJG5eS/mKPtbEY34HFgYjMD3zLOZD+qiIDqA4WwbzLOaZaObzWEfBN8cefkwPncxjK5uwLEjz2XwnGHAEGGUDVkep/kUH498paCUwf0a9I1hARRsVYw4QF8KZz6GKii/W5j3sdijMHmsIwSdzFd0BFIcdqnQihH67g/4hdwDBZeuen6Vt/tFOg5YAl8IA2O1zBB3O4r/XOGkJcN9dvLAFtgGoCQ3go9Zyjdfu4ubGtBH3URkaIBcMCqsh4S5uZrCNeIQdJOuw3BLgq7u44FlmWkjxNAxAr5W4h87Eb2LBnTDi5y3FB8xIDDlGhyyvwDl3gDz2W4qbbHPWF3Q46O9zB2jGYio+WC4wRsFeB/ONxdMKWuR71mCRM0CwkOKKQ5XM3qZ9tnYK2qZQeVvzNDuCUOaLlap3APiVg8HNaSaMj8pi1lJwyqHnOSXrQgMEEIp7IRaNJwW9kcwHAB4Olg0gjwORAQIIc/iUfvX7CMyLB0DhpQ7g2T8g2UEIbqQIcD1W81Me8EdSMD9KxfrYAQIIRVcKvX86EfMBgI+KhG/ofnOQlhhAACHYYH1a4Vb5EQo2J2p+CkRLAgDHUzFfglDcjBHgVmwz3xqgHiuDo/DolR82WqmaL0F42Ol4TDg9x+lBy2K+BFE46wxb/VZkIai4GgKgB1kJNmCp40uQfvpYgiwFFauoeG5h/mVsr4/iDnNGRMHHWcx/pmA7shzMY9cMk8lMnCbMhaCHo9P+ejBpvsNcCprXTQXzP8K8AEEWorj97kpykZ+xubp/rAMA/QAAAABJRU5ErkJggg==`,
  iconSize: [36, 40],
  iconAnchor: [12, 41],
});

const purpleMapMarker = L.icon({
  iconUrl: `data:image/webp;base64,UklGRjwCAABXRUJQVlA4WAoAAAAQAAAAHwAAHwAAQUxQSB0BAAABkKNt27FHT2zbTmlba7BTpeMKUjljG521Adv2TGubzzX4/nnf7ziygIiYAFlsfXxcSe1bvW/f2qFMW4HDN38B6L2RECuhy7+DgsLP9REWvCd/oYrHaR+z2pcoPN67+yEob5qNvNahsDvPxzt3FyhbvU3ib6DcyRIRybyLcjfZpPAlynZx3I7yptSk+gOwyWkz8LHOpOAlcCHmn9iLwKtCk9irKD+nw0Ui5n6iXIszkRWo8v3I1NSxH6CsFOOa1/z36zoz322AetrpZyblT1FVp+dVYtFrATwt87IhufdRHB/ki92xXzjqhFiOPYmCcibOlrR9AfjaKdaDDqDK4RB70vwG3raKi76bYLOvG1L85EmxuDs3Ky4nJLi1CAMAVlA4IPgAAADwBgCdASogACAAPm0wkUakIyGhKA1QgA2JbACsLwFWA2wFgAdJwRFBg1t0SCO+X4hyGGjhbUgZ2ytaDRjLFsewAP7/VsH5O3tQQmtW5RhIyEMNBngh8aMulIdBK9jxKh8WQJ7J9//Lhfv+OeFx1e+49P+Tf8pHvG39H+RpcXwtrTgA/2c3W4rUazOLPD7IeAHURfGzndtRK4G+kZxtPUTgq039ixli+igmGjMweEHD433bejt3HQLR/n0s/EMtghhctaRi5YWQLSwhiHF4ooGJOH596M7AZRs8DdGwycBr/2cVzagT5wR3F30U/pQw36fw/SUTlwyEAA==`,
  iconSize: [32, 32],
  iconAnchor: [11, 33],
});

const publicStationMarker = L.icon({
  iconUrl: `data:image/png;base64,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`,
  iconSize: [12, 22],
  iconAnchor: [6, 22],
});

const MarkerPopupContent = ({ marker }) => {
  const { data } = useQuery(GET_MEASUREMENT_STATION_MEAN, {
    variables: {
      identifier: marker.id,
    },
  });

  const unitLabel = marker.primarytUnit ? ` [${marker.primarytUnit}]` : "";
  const meanData = React.useMemo(
    () => data?.station?.[0]?.mean_24h?.[0] || null,
    [data]
  );

  return (
    <>
      <h6 className="mb-0" dangerouslySetInnerHTML={{ __html: marker.title }} />
      {meanData !== null && meanData.latest_value !== null && (
        <>
          <p className="m-0">
            {formatNumberLocale(Number(meanData.latest_value).toFixed(2))}
            {unitLabel}
          </p>
          <p className="m-0 tooltip-timestamp">
            {timestampToDatetimeString(meanData.timestamp)}
          </p>
        </>
      )}
      {!!marker.description && (
        <p className="m-0 text-500">{marker.description}</p>
      )}
    </>
  );
};

const TogglePopupsButton = ({ allPopupsVisible, setAllPopupsVisible }) => {
  const map = useMap();

  useEffect(() => {
    const CustomControl = L.Control.extend({
      onAdd: () => {
        const button = L.DomUtil.create("button", "custom-leaflet-button");
        button.innerHTML = allPopupsVisible
          ? gettext("Hide info")
          : gettext("Show info");
        button.style.backgroundColor = allPopupsVisible ? "grey" : "white";
        button.style.padding = "5px";
        button.style.borderRadius = "5px";
        button.style.cursor = "pointer";

        L.DomEvent.on(button, "click", () => {
          setAllPopupsVisible(!allPopupsVisible);
        });

        return button;
      },
    });

    const customControl = new CustomControl({ position: "bottomright" });
    map.addControl(customControl);

    return () => {
      map.removeControl(customControl);
    };
  }, [map, allPopupsVisible, setAllPopupsVisible]);

  return null;
};

const MarkerWithPopup = ({ marker, map, allPopupsVisible = false }) => {
  const [refAssigned, setRefAssigned] = React.useState(false);
  let popupRef = React.useRef();
  let markerIcon =
    marker.alertLevel === ALERT_LEVEL_RED ? redMapMarker : mapMarker;
  if (marker.status === "samplepoint") {
    markerIcon = purpleMapMarker;
  }
  if (marker.public) {
    markerIcon = publicStationMarker;
  }

  useEffect(() => {
    if (refAssigned) {
      if (allPopupsVisible && !marker.public) {
        const { lat, lon } = marker;
        // Check if the marker is in the current view
        if (map.getBounds().contains([lat, lon])) {
          popupRef.openPopup();
        }
      } else {
        popupRef.closePopup();
      }
    }
  }, [marker, allPopupsVisible, refAssigned, map]);

  return (
    <Marker
      key={marker.id}
      position={[marker.lat, marker.lon]}
      icon={markerIcon}
      ref={(r) => {
        popupRef = r;
        setRefAssigned(true);
      }}
    >
      <Popup closeOnClick={false} autoClose={false}>
        <MarkerPopupContent marker={marker} />
      </Popup>
    </Marker>
  );
};

const LayerComponent = ({ data, mapLayer, layerData, allPopupsVisible }) => {
  const map = useMap();
  const { config } = useContext(AppContext);

  const { isDark } = config;
  const filter = React.useMemo(
    () =>
      isDark
        ? [
            "invert:98%",
            "grayscale:69%",
            "bright:89%",
            "contrast:111%",
            "hue:205deg",
            "saturate:1000%",
          ]
        : ["bright:101%", "contrast:101%", "hue:23deg", "saturate:225%"],
    [isDark]
  );

  useEffect(() => {
    map.invalidateSize();
  }, [data, map, config]);

  useEffect(() => {
    if (map) {
      const group = new L.featureGroup();
      for (const marker of data) {
        // Hide public markers unless it's the only one (station details page)
        if (marker.public && data.length > 1) {
          continue;
        }
        if (!marker.lat || !marker.lon) {
          continue;
        }
        L.marker([marker.lat, marker.lon]).addTo(group);
      }

      let bounds = group.getBounds();
      // Add some padding so it looks nicer
      bounds = bounds.pad(0.1);

      // A bit of a hack to make sure the map is not zoomed in too much
      map.fitBounds(bounds, { maxZoom: 16 });
    }
  }, [data, map]);

  if (!mapLayer) {
    mapLayer = "default";
  }

  const mapLayerData = layerData?.map_layer?.find(
    (layer) => layer.system_name === mapLayer
  );
  if (!mapLayerData && layerData?.map_layer) {
    // Rename mapLayer to the first available layer
    mapLayer = layerData?.map_layer[0].system_name;
  }

  return (
    <>
      <ScaleControl />
      <LayersControl>
        {!!layerData &&
          layerData.map_layer?.map((layer) => (
            <LayersControl.BaseLayer
              key={layer.id}
              checked={mapLayer === layer.system_name}
              name={layer.display_name}
            >
              <TileLayer
                maxZoom={layer.max_zoom}
                minZoom={layer.min_zoom}
                attribution={layer.attribution}
                url={layer.url}
              />
            </LayersControl.BaseLayer>
          ))}
      </LayersControl>

      {data.map((marker) => (
        <MarkerWithPopup
          key={`marker_${marker.id}`}
          marker={marker}
          map={map}
          allPopupsVisible={allPopupsVisible}
        />
      ))}
    </>
  );
};

const LeafletMap = ({ data, mapLayer, layerData, ...rest }) => {
  const { user } = useAuth();
  const [allPopupsVisible, setAllPopupsVisible] = React.useState(!user?.admin);
  const position = [10.737, 0];
  const {
    config: { isRTL },
  } = useContext(AppContext);
  return (
    <MapContainer
      zoom={isRTL ? 1.8 : 1.5}
      minZoom={isRTL ? 1.8 : 1.3}
      zoomSnap={0.5}
      center={position}
      {...rest}
      radius={200}
      scrollWheelZoom={true}
    >
      <TogglePopupsButton
        allPopupsVisible={allPopupsVisible}
        setAllPopupsVisible={setAllPopupsVisible}
      />
      <LayerComponent
        mapLayer={mapLayer}
        layerData={layerData}
        data={data}
        allPopupsVisible={allPopupsVisible}
      />
    </MapContainer>
  );
};

LeafletMap.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      lat: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      lon: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
      description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    })
  ),
};

LayerComponent.propTypes = {
  data: LeafletMap.propTypes.data,
};

export default LeafletMap;
